import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CellContext } from "@tanstack/react-table";
import { ActionsTable } from "Common/Components";
import FormattedPhoneNumber from "Common/Components/FormatPhoneNumber";
import { useSessionStore } from "Common/Stores";
import { useBranchStore } from "feature/configuration/branch/store";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { Branch } from "interfaces/Branch";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getBranchesByCompany, RemoveBranch } from "services";

export const useBranchList = () => {
  const queryClient = useQueryClient();
  const { session } = useSessionStore();
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [idBranch, setIdBranch] = useState<number>(0);
  const [company, setCompany] = useState<string>("");
  const { t } = useTranslation();
  const { isOpenModal, setIsOpenModal, setIsEdit, setBranch, clearAll } =
    useBranchStore();

  useEffect(() => {
    if (session?.employe) {
      setCompany(session.employe.branch.company.id.toString());
    }
  }, [session?.employe]);

  const columns = useMemo(
    () => [
      {
        header: t("branch.table.columns.index"),
        accessorKey: "index",
        cell: (cell: any) => cell.row.index + 1,
        enableColumnFilter: false,
      },
      {
        header: t("branch.table.columns.description"),
        accessorKey: "description",
        enableColumnFilter: false,
      },
      {
        header: t("branch.table.columns.address"),
        accessorKey: "address",
        enableColumnFilter: false,
      },
      {
        header: t("branch.table.columns.phone"),
        accessorKey: "phonenumber",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: CellContext<any, string>) => (
          <FormattedPhoneNumber phone={getValue()} />
        ),
      },
      {
        header: t("branch.table.columns.company"),
        accessorKey: "company.name",
        enableColumnFilter: false,
      },
      {
        header: t("branch.table.columns.municipality"),
        accessorKey: "municipality.description",
        enableColumnFilter: false,
      },
      {
        header: t("branch.table.columns.actions"),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionsTable
              onClickEdith={() => {
                handleEdit(rowData);
              }}
              onClickDelete={() => {
                handleDelete(rowData);
              }}
            />
          );
        },
      },
    ],
    []
  );

  const handleEdit = (data: Branch) => {
    setIsEdit(true);
    setBranch(data);
    setIsOpenModal(true);
  };

  const handleDelete = (data: Branch) => {
    setIdBranch(data.id);
    setIsDelete(true);
  };

  const { data, refetch: refetchBranch } = useQuery({
    queryKey: ["branch", company],
    queryFn: () => getBranchesByCompany(company),
    enabled: Boolean(company),
  });

  const DeleteBranch = async () => {
    if (!idBranch) return;
    try {
      setIsSubmitting(true);
      const respose = await RemoveBranch(idBranch);

      if (respose.code === "200") {
        queryClient.invalidateQueries({
          queryKey: ["branch"],
        });

        showNotifications(NotificationType.SUCCESS, t("branch.alert.delete"));
        refetchBranch();
        setIsDelete(false);
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("branch.alert.error") + respose.message
        );
      }
    } catch (error) {
      showNotifications(NotificationType.ERROR, t("branch.alert.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    branchList: data?.data,
    columns,
    isOpenModal,
    setIsOpenModal,
    isDelete,
    setIsDelete,
    DeleteBranch,
    refetchBranch,
    company,
    setCompany,
    session,
    clearAll,
    t,
    isSubmitting,
  };
};
